// components/AdminUserActivity.js
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  where,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import "./AdminUserActivity.css";

function AdminUserActivity() {
  const [savedClasses, setSavedClasses] = useState([]);
  const [savedEvents, setSavedEvents] = useState([]);
  const [allClasses, setAllClasses] = useState([]); // NEW: All classes
  const [allEvents, setAllEvents] = useState([]); // NEW: All events
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("classes");
  const [classStats, setClassStats] = useState({});
  const [eventStats, setEventStats] = useState({});
  const [userStats, setUserStats] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]); // NEW: Track admins

  useEffect(() => {
    fetchSavedData();
    fetchAllData(); // NEW: Fetch all classes and events
    checkSuperAdmin();
    fetchAllUsers();
    fetchAdmins(); // NEW: Fetch admin list
  }, []);

  const checkSuperAdmin = async () => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        const superAdminEmails = [
          "joaquinlen@gmail.com",
          "esteban@owlstudio.tv",
        ];
        setIsSuperAdmin(superAdminEmails.includes(currentUser.email));
      }
    } catch (err) {
      console.error("Error checking super admin:", err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const usersRef = collection(db, "users");
      const snapshot = await getDocs(usersRef);

      const userList = [];
      snapshot.forEach((doc) => {
        userList.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchAdmins = async () => {
    try {
      const adminsRef = collection(db, "admins");
      const snapshot = await getDocs(adminsRef);

      const adminList = [];
      snapshot.forEach((doc) => {
        adminList.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setAdmins(adminList);
    } catch (err) {
      console.error("Error fetching admins:", err);
    }
  };

  // Check if a user is admin
  const isUserAdmin = (userId) => {
    return admins.some(
      (admin) => admin.userId === userId || admin.email === userId,
    );
  };

  // Add admin management functions
  const toggleAdminStatus = async (userId, userEmail, userIsAdmin) => {
    if (!isSuperAdmin) return;

    const action = userIsAdmin ? "quitar" : "dar";
    if (
      !window.confirm(
        `¿Está seguro de ${action} permisos de administrador a ${userEmail}?`,
      )
    ) {
      return;
    }

    try {
      if (userIsAdmin) {
        // Remove all admin entries for this user
        const adminsRef = collection(db, "admins");
        const q = query(adminsRef, where("userId", "==", userId));
        const snapshot = await getDocs(q);

        const deletePromises = [];
        snapshot.forEach((document) => {
          deletePromises.push(deleteDoc(doc(db, "admins", document.id)));
        });

        await Promise.all(deletePromises);
      } else {
        // Check if already an admin (prevent duplicates)
        const adminsRef = collection(db, "admins");
        const q = query(adminsRef, where("userId", "==", userId));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          // Add admin
          const auth = getAuth();
          const currentUser = auth.currentUser;

          await addDoc(adminsRef, {
            userId: userId,
            email: userEmail,
            addedAt: new Date().toISOString(),
            addedBy: currentUser?.email || "unknown",
          });
        } else {
          alert("Este usuario ya es administrador");
          return;
        }
      }

      // Refresh data
      fetchAdmins();
      alert(
        `Permisos de administrador ${userIsAdmin ? "quitados" : "otorgados"} exitosamente`,
      );
    } catch (err) {
      console.error("Error toggling admin status:", err);
      alert("Error al cambiar permisos de administrador");
    }
  };

  // NEW: Fetch all classes and events
  const fetchAllData = async () => {
    try {
      // Fetch all classes
      const classesRef = collection(db, "classes");
      const classesSnapshot = await getDocs(classesRef);

      const allClassesData = [];
      classesSnapshot.forEach((doc) => {
        allClassesData.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setAllClasses(allClassesData);

      // Fetch all events
      const eventsRef = collection(db, "events");
      const eventsSnapshot = await getDocs(eventsRef);

      const allEventsData = [];
      eventsSnapshot.forEach((doc) => {
        allEventsData.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setAllEvents(allEventsData);
    } catch (err) {
      console.error("Error fetching all data:", err);
    }
  };

  const fetchSavedData = async () => {
    try {
      setLoading(true);

      // Fetch saved classes
      const savedClassesRef = collection(db, "userSavedClasses");
      const classesQuery = query(savedClassesRef, orderBy("savedAt", "desc"));
      const classesSnapshot = await getDocs(classesQuery);

      const classesData = [];
      classesSnapshot.forEach((document) => {
        const data = document.data();
        classesData.push({
          id: document.id,
          ...data,
          type: "class",
          userEmail: data.userEmail || "Sin email",
        });
      });
      setSavedClasses(classesData);

      // Fetch saved events
      const savedEventsRef = collection(db, "userSavedEvents");
      const eventsQuery = query(savedEventsRef, orderBy("savedAt", "desc"));
      const eventsSnapshot = await getDocs(eventsQuery);

      const eventsData = [];
      eventsSnapshot.forEach((document) => {
        const data = document.data();
        eventsData.push({
          id: document.id,
          ...data,
          type: "event",
          userEmail: data.userEmail || "Sin email",
        });
      });
      setSavedEvents(eventsData);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching saved data:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      processClassStats();
      processEventStats();
      processUserStats();
    }
  }, [loading, allClasses, allEvents]); // Added dependencies

  const processClassStats = () => {
    const stats = {};

    // Start with all classes (including those with no saves)
    allClasses.forEach((classItem) => {
      stats[classItem.id] = {
        classId: classItem.id,
        className: classItem.title || "Sin título",
        classSchedule: classItem.schedule || "",
        classPrice: classItem.price || "",
        users: [],
        count: 0,
      };
    });

    // Add saved data
    savedClasses.forEach((item) => {
      if (!stats[item.classId]) {
        stats[item.classId] = {
          classId: item.classId,
          className: item.className || "Sin título",
          classSchedule: item.classSchedule || "",
          classPrice: item.classPrice || "",
          users: [],
          count: 0,
        };
      }

      stats[item.classId].users.push({
        userId: item.userId,
        userEmail: item.userEmail,
        savedAt: item.savedAt,
      });
      stats[item.classId].count++;
    });

    setClassStats(stats);
  };

  const processEventStats = () => {
    const stats = {};

    // Start with all events (including those with no saves)
    allEvents.forEach((eventItem) => {
      stats[eventItem.id] = {
        eventId: eventItem.id,
        eventTitle: eventItem.title || "Sin título",
        eventDate: eventItem.date || "",
        eventTime: eventItem.time || "",
        users: [],
        count: 0,
      };
    });

    // Add saved data
    savedEvents.forEach((item) => {
      if (!stats[item.eventId]) {
        stats[item.eventId] = {
          eventId: item.eventId,
          eventTitle: item.eventTitle || "Sin título",
          eventDate: item.eventDate || "",
          eventTime: item.eventTime || "",
          users: [],
          count: 0,
        };
      }

      stats[item.eventId].users.push({
        userId: item.userId,
        userEmail: item.userEmail,
        savedAt: item.savedAt,
      });
      stats[item.eventId].count++;
    });

    setEventStats(stats);
  };

  const processUserStats = () => {
    const stats = {};

    // Process classes
    savedClasses.forEach((item) => {
      if (!stats[item.userId]) {
        stats[item.userId] = {
          userId: item.userId,
          userEmail: item.userEmail,
          isAdmin: isUserAdmin(item.userId) || isUserAdmin(item.userEmail),
          savedClasses: [],
          savedEvents: [],
          totalSaved: 0,
        };
      }

      stats[item.userId].savedClasses.push({
        id: item.id,
        classId: item.classId,
        className: item.className,
        savedAt: item.savedAt,
      });
      stats[item.userId].totalSaved++;
    });

    // Process events
    savedEvents.forEach((item) => {
      if (!stats[item.userId]) {
        stats[item.userId] = {
          userId: item.userId,
          userEmail: item.userEmail,
          isAdmin: isUserAdmin(item.userId) || isUserAdmin(item.userEmail),
          savedClasses: [],
          savedEvents: [],
          totalSaved: 0,
        };
      }

      stats[item.userId].savedEvents.push({
        id: item.id,
        eventId: item.eventId,
        eventTitle: item.eventTitle,
        savedAt: item.savedAt,
      });
      stats[item.userId].totalSaved++;
    });

    // Add users who have saved nothing but exist in the system
    users.forEach((user) => {
      if (!stats[user.uid]) {
        stats[user.uid] = {
          userId: user.uid,
          userEmail: user.email,
          isAdmin: isUserAdmin(user.uid) || isUserAdmin(user.email),
          savedClasses: [],
          savedEvents: [],
          totalSaved: 0,
        };
      }
    });

    setUserStats(stats);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-AR", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch (err) {
      return dateString;
    }
  };

  const refreshData = () => {
    fetchSavedData();
    fetchAllData();
    fetchAdmins();
    fetchAllUsers();
  };

  const getSortedClassStats = () => {
    return Object.values(classStats).sort((a, b) => b.count - a.count);
  };

  const getSortedEventStats = () => {
    return Object.values(eventStats).sort((a, b) => b.count - a.count);
  };

  const getSortedUserStats = () => {
    return Object.values(userStats).sort((a, b) => b.totalSaved - a.totalSaved);
  };

  return (
    <div className="admin-activity">
      <div className="admin-header">
        <h1>Actividad de Usuarios</h1>
        <p>Ver qué clases y eventos han guardado los usuarios</p>
      </div>

      <div className="admin-controls">
        <button onClick={refreshData} className="refresh-btn">
          🔄 Actualizar datos
        </button>

        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-number">{allClasses.length}</span>
            <span className="stat-label">Clases totales</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{allEvents.length}</span>
            <span className="stat-label">Eventos totales</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{Object.keys(userStats).length}</span>
            <span className="stat-label">Usuarios registrados</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{admins.length}</span>
            <span className="stat-label">Administradores</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="activity-tabs">
        <button
          className={`tab ${activeTab === "classes" ? "active" : ""}`}
          onClick={() => setActiveTab("classes")}
        >
          Clases
          <span className="tab-count">{Object.keys(classStats).length}</span>
        </button>
        <button
          className={`tab ${activeTab === "events" ? "active" : ""}`}
          onClick={() => setActiveTab("events")}
        >
          Eventos
          <span className="tab-count">{Object.keys(eventStats).length}</span>
        </button>
        <button
          className={`tab ${activeTab === "users" ? "active" : ""}`}
          onClick={() => setActiveTab("users")}
        >
          Usuarios
          <span className="tab-count">{Object.keys(userStats).length}</span>
        </button>
      </div>

      {/* Content */}
      <div className="activity-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando datos...</p>
          </div>
        ) : activeTab === "classes" ? (
          <div className="classes-section">
            {getSortedClassStats().length === 0 ? (
              <div className="empty-state">
                <p>No hay clases en el sistema.</p>
              </div>
            ) : (
              getSortedClassStats().map((classItem) => (
                <div key={classItem.classId} className="item-card">
                  <div className="item-header">
                    <h3>{classItem.className}</h3>
                    <div className="item-meta">
                      <span className="meta-item">
                        {classItem.classSchedule}
                      </span>
                      {classItem.classPrice && (
                        <span className="meta-item">
                          {classItem.classPrice}
                        </span>
                      )}
                      <span
                        className={`save-count ${classItem.count === 0 ? "no-saves" : ""}`}
                      >
                        📌 {classItem.count} guardado(s)
                      </span>
                    </div>
                  </div>

                  {classItem.users.length > 0 ? (
                    <div className="users-list">
                      <h4>Usuarios que guardaron:</h4>
                      <div className="users-grid">
                        {classItem.users.map((user, index) => (
                          <div key={index} className="user-badge">
                            <div className="user-email">{user.userEmail}</div>
                            <div className="user-date">
                              {formatDate(user.savedAt)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="no-users">
                      <p className="no-users-text">
                        Ningún usuario ha guardado esta clase.
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : activeTab === "events" ? (
          <div className="events-section">
            {getSortedEventStats().length === 0 ? (
              <div className="empty-state">
                <p>No hay eventos en el sistema.</p>
              </div>
            ) : (
              getSortedEventStats().map((eventItem) => (
                <div key={eventItem.eventId} className="item-card">
                  <div className="item-header">
                    <h3>{eventItem.eventTitle}</h3>
                    <div className="item-meta">
                      <span className="meta-item">
                        {formatDate(eventItem.eventDate)}
                      </span>
                      <span className="meta-item">{eventItem.eventTime}</span>
                      <span
                        className={`save-count ${eventItem.count === 0 ? "no-saves" : ""}`}
                      >
                        📌 {eventItem.count} guardado(s)
                      </span>
                    </div>
                  </div>

                  {eventItem.users.length > 0 ? (
                    <div className="users-list">
                      <h4>Usuarios que guardaron:</h4>
                      <div className="users-grid">
                        {eventItem.users.map((user, index) => (
                          <div key={index} className="user-badge">
                            <div className="user-email">{user.userEmail}</div>
                            <div className="user-date">
                              {formatDate(user.savedAt)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="no-users">
                      <p className="no-users-text">
                        Ningún usuario ha guardado este evento.
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="users-section">
            {getSortedUserStats().length === 0 ? (
              <div className="empty-state">
                <p>No hay datos de usuarios.</p>
              </div>
            ) : (
              getSortedUserStats().map((user) => {
                const userIsAdmin =
                  user.isAdmin ||
                  isUserAdmin(user.userId) ||
                  isUserAdmin(user.userEmail);

                return (
                  <div key={user.userId} className="user-card">
                    <div className="user-header">
                      <h3>{user.userEmail}</h3>
                      <div className="user-stats">
                        <span className="stat-badge">
                          {user.savedClasses.length} clases
                        </span>
                        <span className="stat-badge">
                          {user.savedEvents.length} eventos
                        </span>
                        <span className="total-badge">
                          {user.totalSaved} total
                        </span>

                        {userIsAdmin && (
                          <span className="admin-badge">👑 Administrador</span>
                        )}

                        {/* Add Admin Toggle Button - Only show for super admins */}
                        {isSuperAdmin && (
                          <button
                            onClick={() =>
                              toggleAdminStatus(
                                user.userId,
                                user.userEmail,
                                userIsAdmin,
                              )
                            }
                            className={`admin-toggle-btn ${userIsAdmin ? "remove" : "add"}`}
                          >
                            {userIsAdmin ? "✕ Quitar admin" : "👑 Hacer admin"}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="user-content">
                      {user.savedClasses.length > 0 && (
                        <div className="saved-section">
                          <h4>Clases guardadas:</h4>
                          <div className="saved-items">
                            {user.savedClasses.map((classItem) => (
                              <div key={classItem.id} className="saved-item">
                                <span className="item-title">
                                  {classItem.className}
                                </span>
                                <span className="item-date">
                                  {formatDate(classItem.savedAt)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {user.savedEvents.length > 0 && (
                        <div className="saved-section">
                          <h4>Eventos guardados:</h4>
                          <div className="saved-items">
                            {user.savedEvents.map((eventItem) => (
                              <div key={eventItem.id} className="saved-item">
                                <span className="item-title">
                                  {eventItem.eventTitle}
                                </span>
                                <span className="item-date">
                                  {formatDate(eventItem.savedAt)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {user.savedClasses.length === 0 &&
                        user.savedEvents.length === 0 && (
                          <div className="no-saved-items">
                            <p>Este usuario no ha guardado ningún contenido.</p>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUserActivity;
